import { useEffect, useMemo, useRef, type FC } from "react";
import { TriangleAlert } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { DashboardExportButton } from "src/components/common";
import { useDashboardPdfExport } from "src/hooks/UseDashboardPdfExport";
import EngagementSection from "./EngagementSection";
import DriversSection from "./DriversSection";
import NextStepsSection from "./NextStepsSection";
import { REPORT_KEY, ReportTag } from "./ReportPrimitives";
import ReportTabBar, { type ReportTab } from "./ReportTabBar";
import SentimentSection from "./SentimentSection";
import SummarySection from "./SummarySection";
import VoicesSection from "./VoicesSection";
import WordCloudSection from "./WordCloudSection";
import type { BrandListeningReportData, BrandSummary } from "../Types/ListeningReportTypes";
import { getDriverStatus } from "../Utils/ListeningReportUtils";
import "../Styles/ListeningReport.scss";

const VersusBrand: FC<{ role: string; brand: BrandSummary; end?: boolean }> = ({ role, brand, end }) => {
  const { t } = useTranslation();
  return (
    <div className={`br__vs-brand${end ? " br__vs-brand--end" : ""}`}>
      <span className="br__vs-role">{role}</span>
      <span className="br__vs-name">{brand.name}</span>
      <span className="br__vs-count">
        {t(`${REPORT_KEY}.versus.scored`, "{{count}} items scored · {{sources}}", {
          count: brand.scoredItems,
          sources: brand.sources.join(", "),
        })}
      </span>
    </div>
  );
};

const DASHBOARD_NAME = "Brand Listening Report";

interface ListeningReportProps {
  report: BrandListeningReportData;
  autoDownload?: boolean;
}

const ListeningReport: FC<ListeningReportProps> = ({ report, autoDownload = false }) => {
  const { t } = useTranslation();
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const hasAutoDownloaded = useRef(false);
  const { exportPdf } = useDashboardPdfExport();
  const dateRange = useMemo(
    () => ({ startDate: report.generatedOn, endDate: report.generatedOn }),
    [report.generatedOn]
  );

  useEffect(() => {
    if (!autoDownload || hasAutoDownloaded.current) {return;}
    hasAutoDownloaded.current = true;
    void exportPdf({ containerEl: pdfContainerRef.current, dashboardName: DASHBOARD_NAME, dateRange });
  }, [autoDownload, exportPdf, dateRange]);
  const { primaryBrand, competitor } = report;
  const names = { primary: primaryBrand.name, competitor: competitor?.name ?? "" };
  const allComments = report.voices.flatMap(column => column.quotes.map(quote => quote.text));
  const riskThemeCount = report.drivers.filter(driver => getDriverStatus(driver) === "risk").length;

  const tabs = useMemo<ReportTab[]>(
    () => [
      { id: "br-summary", label: t(`${REPORT_KEY}.tabs.summary`, "Summary") },
      { id: "br-sentiment", label: t(`${REPORT_KEY}.tabs.sentiment`, "Sentiment") },
      { id: "br-engagement", label: t(`${REPORT_KEY}.tabs.engagement`, "Engagement") },
      {
        id: "br-drivers",
        label: t(`${REPORT_KEY}.tabs.drivers`, "Drivers"),
        badge: riskThemeCount,
        badgeTitle: t(`${REPORT_KEY}.tabs.driversBadge`, "Themes at risk"),
      },
      { id: "br-voices", label: t(`${REPORT_KEY}.tabs.voices`, "Customer Voice") },
      { id: "br-words", label: t(`${REPORT_KEY}.tabs.words`, "Word Cloud") },
      { id: "br-actions", label: t(`${REPORT_KEY}.tabs.actions`, "Next Steps") },
    ],
    [t, riskThemeCount]
  );

  return (
    <div className="br" ref={pdfContainerRef}>
      <ReportTabBar tabs={tabs} ariaLabel={t(`${REPORT_KEY}.tabs.label`, "Report sections")} />

      <div className="br__head" data-pdf-block="br-header">
        <div>
          <h1>{report.title}</h1>
          <p className="br__sub">
            <Trans
              i18nKey={competitor ? `${REPORT_KEY}.subtitle` : `${REPORT_KEY}.subtitleSingle`}
              defaults={
                competitor
                  ? "Understand what customers say about <b>{{primary}}</b> on social, and how it compares with its competitor <b>{{competitor}}</b>."
                  : "Understand what customers say about <b>{{primary}}</b> on social."
              }
              values={names}
              components={{ b: <b /> }}
            />
          </p>
        </div>
        <DashboardExportButton
          dashboardName={DASHBOARD_NAME}
          dateRange={dateRange}
          containerRef={pdfContainerRef}
        />
      </div>

      <div className="br__meta" data-pdf-block="br-meta">
        <ReportTag tone="blue">{competitor
            ? t(`${REPORT_KEY}.analysisType`, "Competitor Analysis")
            : t(`${REPORT_KEY}.analysisTypeBrand`, "Brand Analysis")}</ReportTag>
        {report.meta.map(item => (
          <ReportTag key={item.label}>
            {item.label} <b>{item.value}</b>
          </ReportTag>
        ))}
      </div>

      {competitor && (
        <div className="br__versus" data-pdf-block="br-versus" aria-label={t(`${REPORT_KEY}.versus.label`, "Brands compared in this report")}>
          <VersusBrand role={t(`${REPORT_KEY}.versus.yourBrand`, "Your brand")} brand={primaryBrand} />
          <span className="br__vs-mid">{t(`${REPORT_KEY}.versus.vs`, "vs")}</span>
          <VersusBrand role={t(`${REPORT_KEY}.versus.competitor`, "Competitor")} brand={competitor} end />
          <p className="br__vs-note">
            {t(`${REPORT_KEY}.versus.noteIntro`, "Each data section below is labelled with what it covers:")}{" "}
            <ReportTag small tone="blue">
              {t(`${REPORT_KEY}.scope.versus`, "{{primary}} vs {{competitor}}", names)}
            </ReportTag>{" "}
            {t(`${REPORT_KEY}.versus.noteBoth`, "compares both brands,")}{" "}
            <ReportTag small>{t(`${REPORT_KEY}.scope.only`, "{{primary}} only", names)}</ReportTag>{" "}
            {t(`${REPORT_KEY}.versus.noteOnly`, "looks at {{primary}} alone.", names)}
          </p>
        </div>
      )}

      {[report.unavailableNotice, report.limitedDataNotice].map(
        notice =>
          notice && (
            <div className="br__notice" role="alert" data-pdf-block="br-notice" key={notice.title}>
              <TriangleAlert className="br-ic" aria-hidden />
              <div>
                <b>{notice.title}</b>
                <p>{notice.body}</p>
              </div>
            </div>
          )
      )}

      <SummarySection kpis={report.kpis} readout={report.readout} />
      <SentimentSection
        competitive={report.competitiveSentiment}
        platform={report.platformSentiment}
        sourceCoverage={report.sourceCoverage}
        sourceCoverageNote={report.sourceCoverageNote}
        primaryName={primaryBrand.name}
        competitorName={competitor?.name}
      />
      <EngagementSection
        rows={report.engagementBySource}
        topPosts={report.topPosts}
        primaryName={primaryBrand.name}
        competitorName={competitor?.name}
      />
      <DriversSection
        primary={primaryBrand}
        competitor={competitor}
        drivers={report.drivers}
        insights={report.driverInsights}
        themeComparison={report.themeComparison}
        wins={report.wins}
      />
      <VoicesSection columns={report.voices} primaryName={primaryBrand.name} />
      <WordCloudSection words={report.words} comments={allComments} primaryName={primaryBrand.name} />
      <NextStepsSection steps={report.nextSteps} />

      <div className="br__foot">
        <span>DataTram</span>
        <span>
          {t(`${REPORT_KEY}.footer`, "Generated in DataTram on {{date}}. Request ID {{requestId}}.", {
            date: report.generatedOn,
            requestId: report.requestId,
          })}
        </span>
      </div>
    </div>
  );
};

export default ListeningReport;
